import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Dialog, Input, Select } from '@dreamtree-org/twreact-ui';
import { UserPlus } from 'lucide-react';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';
import DataTable from '@/components/DataTable';
import Can from '@rbac/Can';
import { useAuth } from '@rbac/useCan';
import { adminRequest, errorOf } from '@/lib/tenantAuth';

/**
 * C04/C05 — the company's users, scoped by the server to what the viewer may see (a branch
 * administrator sees their branch only). Invitations bind a branch and a role and produce a
 * one-time link; email delivery arrives with company SMTP in a later phase.
 */
function InviteDialog({ open, onClose, roles, branches, defaultBranchId, onInvited }) {
  const [form, setForm] = useState({ email: '', fullName: '', roleId: '', branchId: defaultBranchId || '', roleScope: 'branch' });
  const [link, setLink] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const auth = useAuth();

  useEffect(() => {
    if (!open) return;
    setForm({ email: '', fullName: '', roleId: '', branchId: defaultBranchId || '', roleScope: 'branch' });
    setLink(null);
    setErr('');
  }, [open, defaultBranchId]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      const res = await adminRequest('post', '/users/invitations', form);
      setLink(`${window.location.origin}/c/${auth.company.slug}/accept-invite?token=${encodeURIComponent(res.token)}`);
      onInvited();
    } catch (ex) {
      setErr(errorOf(ex).message);
    } finally {
      setBusy(false);
    }
  };

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  return (
    <Dialog isOpen={open} onClose={onClose} title="Invite a user" size="md">
      {link ? (
        <div className="space-y-3">
          <Alert variant="warning" title="Copy this link now — it is shown only once">
            Send it to the person directly. It expires in 7 days and works once.
          </Alert>
          <code className="block text-xs break-all bg-secondary-100 dark:bg-secondary-900 p-2 rounded select-all">{link}</code>
          <div className="flex justify-end"><Button onClick={onClose}>Done</Button></div>
        </div>
      ) : (
        <form className="space-y-3" onSubmit={submit}>
          {err && <Alert variant="error">{err}</Alert>}
          <Input label="Email" type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} required />
          <Input label="Name (optional)" value={form.fullName} onChange={(e) => set({ fullName: e.target.value })} />
          <Select label="Branch" value={form.branchId} onChange={(v) => set({ branchId: v })} options={branches.map((b) => ({ value: b.id, label: `${b.code} — ${b.name}` }))} />
          <Select label="Role" value={form.roleId} onChange={(v) => set({ roleId: v })} options={roles.map((r) => ({ value: r.id, label: r.name }))} />
          <Select
            label="Where the role applies"
            value={form.roleScope}
            onChange={(v) => set({ roleScope: v })}
            options={[{ value: 'branch', label: 'Only in that branch' }, { value: 'company', label: 'Company-wide' }]}
          />
          <p className="text-xs text-secondary-500">You can only grant permissions you hold yourself at that scope.</p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy || !form.roleId || !form.branchId}>{busy ? 'Inviting…' : 'Create invitation'}</Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

export default function UsersAdmin() {
  const auth = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [inviting, setInviting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [u, r, b] = await Promise.all([
        adminRequest('get', '/users'),
        adminRequest('get', '/roles').catch(() => []),
        adminRequest('get', '/branches').catch(() => auth.branches || []),
      ]);
      setUsers(u);
      setRoles(r);
      setBranches(b);
    } catch (ex) {
      setErr(errorOf(ex).message);
    }
  }, [auth.branches]);

  useEffect(() => { load(); }, [load]);

  const branchCode = (id) => (branches.find((b) => b.id === id) || {}).code || '—';
  const act = async (fn, message) => {
    setErr('');
    setNotice('');
    try {
      await fn();
      setNotice(message);
      await load();
    } catch (ex) {
      setErr(errorOf(ex).message);
    }
  };

  const columns = [
    { key: 'email', label: 'User', render: (u) => <div><div className="font-medium">{u.fullName || u.email}</div><div className="text-xs text-secondary-500">{u.email}</div></div> },
    { key: 'status', label: 'Status', render: (u) => <Badge size="sm" variant={u.status === 'active' ? 'success' : 'secondary'}>{u.status}</Badge> },
    { key: 'branches', label: 'Branches', searchable: false, render: (u) => u.branches.filter((m) => m.status === 'active').map((m) => branchCode(m.branchId)).join(', ') },
    {
      key: 'roles', label: 'Roles', searchable: false,
      render: (u) => (
        <div className="flex flex-wrap gap-1">
          {u.roles.map((r) => (
            <Badge key={r.assignmentId} size="sm" variant="primary">
              {r.name}{r.scope === 'branch' ? ` @ ${branchCode(r.branchId)}` : ' (company)'}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      key: 'actions', label: '', searchable: false, sortable: false,
      render: (u) => (u.id === auth.id ? null : (
        <Can module="users" action="manage">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => act(() => adminRequest('post', `/users/${u.id}/status`, { status: u.status === 'active' ? 'disabled' : 'active' }), u.status === 'active' ? 'User disabled — their sessions ended' : 'User re-enabled')}
          >
            {u.status === 'active' ? 'Disable' : 'Enable'}
          </Button>
        </Can>
      )),
    },
  ];

  return (
    <PageShell className="space-y-3">
      <PageHeader
        title="Users"
        subtitle={`People in ${auth.company ? auth.company.displayName : 'this company'}. Invitations bind a branch and a role.`}
        actions={<Can module="users" action="invite"><Button size="sm" onClick={() => setInviting(true)}><UserPlus size={14} className="mr-1 inline" /> Invite</Button></Can>}
      />
      {err && <Alert variant="error" dismissible onDismiss={() => setErr('')}>{err}</Alert>}
      {notice && <Alert variant="success" dismissible onDismiss={() => setNotice('')}>{notice}</Alert>}
      <Card className="p-0">
        <DataTable data={users} columns={columns} search={{ placeholder: 'Search users…', keys: ['email', 'fullName'] }} emptyText="No users yet." />
      </Card>
      <InviteDialog open={inviting} onClose={() => setInviting(false)} roles={roles} branches={branches} defaultBranchId={auth.activeBranchId} onInvited={load} />
    </PageShell>
  );
}
